import { DayOfWeekEnum, PlayerFinderTimeOfDayEnum } from 'types/generated/client';
import { dayOfWeekPluralLabel } from 'utils/shared/dayOfWeek';

/**
 * The "When are you usually available?" chips shared by the Find onboarding
 * step and the home Filters sheet. Shorthands expand to real days at save/apply
 * time, so they never reach the database. See screens/Find/DB-DECISION-ONBOARDING.md.
 */
export enum AvailabilityDayShorthand {
  Weekends = 'WEEKENDS',
  AnyDay = 'ANY_DAY',
}

/** A day chip is either a real weekday or one of the two shorthands. */
export type AvailabilityDay = DayOfWeekEnum | AvailabilityDayShorthand;

/** Monday-first because the design lists the working week before the weekend shorthands. */
export const CHIP_DAY_ORDER: DayOfWeekEnum[] = [
  DayOfWeekEnum.Monday,
  DayOfWeekEnum.Tuesday,
  DayOfWeekEnum.Wednesday,
  DayOfWeekEnum.Thursday,
  DayOfWeekEnum.Friday,
  DayOfWeekEnum.Saturday,
  DayOfWeekEnum.Sunday,
];

export const AVAILABILITY_DAY_OPTIONS: { value: AvailabilityDay; label: string }[] = [
  ...CHIP_DAY_ORDER.map((value) => ({ value, label: dayOfWeekPluralLabel(value) })),
  { value: AvailabilityDayShorthand.Weekends, label: 'Weekends' },
  { value: AvailabilityDayShorthand.AnyDay, label: "I'm flexible" },
];

export const TIME_OF_DAY_OPTIONS = [
  { value: PlayerFinderTimeOfDayEnum.Morning, label: 'Mornings' },
  { value: PlayerFinderTimeOfDayEnum.Afternoon, label: 'Afternoons' },
  { value: PlayerFinderTimeOfDayEnum.Evening, label: 'Evenings' },
  { value: PlayerFinderTimeOfDayEnum.Flexible, label: "I'm flexible" },
];

export type TimesByDay = Partial<Record<AvailabilityDay, PlayerFinderTimeOfDayEnum[]>>;

/** JS `Date#getDay()` (0 = Sunday) → the matching weekday enum. */
export const dayOfWeekFromDate = (date: Date): DayOfWeekEnum =>
  [
    DayOfWeekEnum.Sunday,
    DayOfWeekEnum.Monday,
    DayOfWeekEnum.Tuesday,
    DayOfWeekEnum.Wednesday,
    DayOfWeekEnum.Thursday,
    DayOfWeekEnum.Friday,
    DayOfWeekEnum.Saturday,
  ][date.getDay()];

export const WEEKEND_DAYS: DayOfWeekEnum[] = [DayOfWeekEnum.Saturday, DayOfWeekEnum.Sunday];

/** One chip → real weekdays: the two shorthands fan out, a real day maps to itself. */
export const expandAvailabilityDay = (day: AvailabilityDay): DayOfWeekEnum[] => {
  if (day === AvailabilityDayShorthand.Weekends) return WEEKEND_DAYS;
  if (day === AvailabilityDayShorthand.AnyDay) return CHIP_DAY_ORDER;
  return [day];
};

/** Filter semantics: no chips (or "I'm flexible") = every day; otherwise the union, de-duped. */
export const expandAvailabilityDays = (days: AvailabilityDay[]): DayOfWeekEnum[] => {
  if (days.length === 0) return CHIP_DAY_ORDER;
  const set = new Set(days.flatMap(expandAvailabilityDay));
  return CHIP_DAY_ORDER.filter((day) => set.has(day));
};

/**
 * Time buckets that apply to a concrete weekday, merging the day's own chips with
 * any shorthand (Weekends / I'm flexible) covering it. Empty or FLEXIBLE = any time.
 */
export const timesForDay = (
  day: DayOfWeekEnum,
  days: AvailabilityDay[],
  timesByDay: TimesByDay,
): PlayerFinderTimeOfDayEnum[] => {
  const keys: AvailabilityDay[] = days.includes(day) ? [day] : [];
  if (WEEKEND_DAYS.includes(day) && days.includes(AvailabilityDayShorthand.Weekends)) {
    keys.push(AvailabilityDayShorthand.Weekends);
  }
  if (days.includes(AvailabilityDayShorthand.AnyDay)) keys.push(AvailabilityDayShorthand.AnyDay);

  const times = new Set<PlayerFinderTimeOfDayEnum>();
  keys.forEach((key) => (timesByDay[key] ?? []).forEach((time) => times.add(time)));
  if (times.size === 0 || times.has(PlayerFinderTimeOfDayEnum.Flexible)) return [];
  return Array.from(times);
};
